import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import copy from 'copy-to-clipboard';
import { useState } from 'react';
import { MdContentCopy, MdOutlineDelete, MdOutlineModeEdit } from 'react-icons/md';
import { Webhook } from '@/apis/agency/webhooks/types';
import { formatDate } from '@/utils/helperFunctions';

interface ColumnsProps {
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  isDeleting?: boolean;
}

function truncateUrl(url: string, max = 28) {
  if (url.length <= max) return url;
  return `${url.slice(0, max)}...`;
}

function CopyEndpointButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <Tooltip title={copied ? 'Copied' : 'Copy'} open={copied || undefined} onClose={() => setCopied(false)}>
      <IconButton
        size='small'
        sx={{ color: '#777777', p: 0.5 }}
        onClick={() => {
          copy(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        aria-label='Copy endpoint URL'
      >
        <MdContentCopy size={16} />
      </IconButton>
    </Tooltip>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <Chip
      label={active ? 'Active' : 'Inactive'}
      size='small'
      sx={{
        height: 22,
        fontWeight: 500,
        bgcolor: active ? 'success.light' : 'error.light',
        color: active ? 'success.main' : 'error.main',
        borderRadius: '14px',
        '& .MuiChip-label': { px: 1 }
      }}
    />
  );
}

export function getWebhookColumns({ onEdit, onDelete, isDeleting }: ColumnsProps): ColumnDef<Webhook>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Destination',
      cell: ({ row }) => (
        <Box>
          <Typography fontWeight={500} color='text.primary' lineHeight={1.3}>
            {row.original.name}
          </Typography>
          {row.original.description && (
            <Typography fontSize={12} color='text.secondary' noWrap sx={{ maxWidth: 280, mt: 0.15 }} lineHeight={1.3}>
              {row.original.description}
            </Typography>
          )}
        </Box>
      )
    },
    {
      accessorKey: 'endpoint_url',
      header: 'Endpoint URL',
      cell: ({ row }) => (
        <Box display='flex' alignItems='center' gap={0.75}>
          <Typography color='text.primary' sx={{ maxWidth: 220 }} noWrap title={row.original.endpoint_url}>
            {truncateUrl(row.original.endpoint_url)}
          </Typography>
          <CopyEndpointButton url={row.original.endpoint_url} />
        </Box>
      )
    },
    {
      accessorKey: 'events',
      header: 'Events',
      cell: ({ row }) => {
        const events = row.original.events || [];
        const overflow = events.length - 2;

        return (
          <Box display='flex' gap={0.75} flexWrap='nowrap' alignItems='center'>
            {events.slice(0, 2).map((event) => (
              <Chip
                key={event}
                label={event}
                size='small'
                sx={{
                  height: 22,
                  fontWeight: 500,
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  px: 1,
                  py: 1.5,
                  borderRadius: '14px',
                  textTransform: 'none',
                  '& .MuiChip-label': { px: 0.75, textTransform: 'none' }
                }}
              />
            ))}
            {overflow > 0 && (
              <Tooltip
                arrow
                placement='top'
                title={
                  <Box component='ul' sx={{ m: 0, pl: 2, py: 0.5 }}>
                    {events.map((event) => (
                      <Box component='li' key={event} sx={{ fontSize: 12, lineHeight: 1.6 }}>
                        {event}
                      </Box>
                    ))}
                  </Box>
                }
              >
                <Typography
                  component='span'
                  fontSize={12}
                  fontWeight={500}
                  color='primary.main'
                  sx={{ textDecoration: 'underline', whiteSpace: 'nowrap', cursor: 'pointer' }}
                >
                  +{overflow}
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge active={Boolean(row.original.status)} />
    },
    {
      accessorKey: 'created_at',
      header: 'Created On',
      cell: ({ row }) => (
        <Typography color='text.primary' noWrap>
          {formatDate(row.original.created_at)}
        </Typography>
      )
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => (
        <Box display='flex' gap={0.25}>
          <IconButton sx={{ color: '#777777' }} onClick={() => onEdit(row.original.id)}>
            <MdOutlineModeEdit />
          </IconButton>
          <IconButton
            sx={{ color: '#E53935' }}
            disabled={isDeleting}
            onClick={async () => {
              onDelete(row.original.id);
            }}
          >
            <MdOutlineDelete />
          </IconButton>
        </Box>
      )
    }
  ];
}
